import { Loading } from '@/components/loading/Loading';
import { Title } from '@/components/phone/Title';
import { useCurrentUser, useTellers } from '@/hooks';
import { setupApp } from '@/server/setupApp';
import type { TellerViewModel } from '@/viewModels';
import {
  getActiveOnlineStatuses,
  getActiveRooms,
  getAllRankings,
  getFutureSchedules,
  getHighlightReviewsOrderByTextCount,
  getListedFeatures,
  getPlansInIds,
  getRecommendedTellers,
  getTellersInIds,
  getViews,
} from '@raysee/firestore';
import { FilterIcon, SortIcon } from '@raysee/icon';
import { RankingType } from '@raysee/schema';
import { flatten } from '@raysee/utility';
import type { InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import {
  Container,
  Footer,
  getTellerStatus,
  Header,
  Main,
  TabMenu,
  TellerGridList,
  TellerStatus,
  withLayout,
} from '../../../components';

interface FormValues {
  keyword: string;
}
type filterType = 'now' | 'book';
type sortType = 'debut' | 'num';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const PER_LOAD_NUM = 30;

export const getServerSideProps = async () => {
  setupApp();

  const [recommendedTellers, reviews, rankings, schedules, rooms, views, features, onlineStatuses] =
    await Promise.all([
      getRecommendedTellers(),
      getHighlightReviewsOrderByTextCount(),
      getAllRankings(),
      getFutureSchedules(),
      getActiveRooms(),
      getViews(),
      getListedFeatures(),
      getActiveOnlineStatuses(),
    ]);

  const planIds = Array.from(
    new Set([
      ...rankings
        .map((ranking) => (ranking.type === RankingType.PlansBySatisfaction ? ranking.planIds : []))
        .reduce(flatten, []),
      ...features.map((feature) => feature.rankingItemIds).reduce(flatten, []),
    ]),
  );
  const plans = await getPlansInIds(planIds);

  const recommendedTellerIds = recommendedTellers.map((teller) => teller.id);
  const tellerIds = Array.from(
    new Set([
      ...reviews.map((review) => review.tellerId),
      ...rankings
        .map((ranking) =>
          ranking.type !== RankingType.PlansBySatisfaction ? ranking.tellerIds : [],
        )
        .reduce(flatten),
      ...schedules.map((schedule) => schedule.tellerId),
      ...plans.map((plan) => plan.tellerId),
      ...onlineStatuses.map((onlineStatus) => onlineStatus.tellerId),
    ]),
  ).filter((tellerId) => !recommendedTellerIds.includes(tellerId));
  const tellers = await getTellersInIds(tellerIds).then((tellers) => [
    ...tellers,
    ...recommendedTellers,
  ]);

  return {
    props: {
      tellers,
      reviews,
      schedules,
      rooms,
      views,
    },
  };
};

const Page: NextPage<Props> = (props) => {
  const tellers = useTellers(props);
  const { user } = useCurrentUser();
  // 検索条件、ソート条件
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState<filterType[]>([]);
  const [sort, setSort] = useState<sortType>('debut');

  const handleSubmit = async (values: FormValues) => {
    setShowNum(PER_LOAD_NUM);
    setKeyword(values.keyword);
  };
  const onFilter = (key: filterType) => {
    setShowNum(PER_LOAD_NUM);
    setFilter((p) => (p.includes(key) ? p.filter((item) => item !== key) : [...p, key]));
  };
  const onSort = (key: sortType) => {
    setShowNum(PER_LOAD_NUM);
    setSort(key);
  };

  // 検索条件やソートを加味した一覧
  const filterAllList = useMemo(() => {
    let tmpTeller = [...tellers];

    // フィルター
    if (filter.length > 0) {
      tmpTeller = tmpTeller.filter((t) => {
        const status = getTellerStatus(t, t.schedules, t.rooms);
        const isShow =
          (filter.includes('now') && status === TellerStatus.Standby) ||
          (filter.includes('book') && status === TellerStatus.Available);
        return isShow;
      });
    }

    // キーワード
    if (keyword) {
      tmpTeller = tmpTeller.filter(
        (t) =>
          t.name.includes(keyword) ||
          t.categories.includes(keyword) ||
          t.catchphrase?.includes(keyword) ||
          t.description.includes(keyword),
      );
    }

    // ソート
    const sortFn =
      sort === 'debut'
        ? // 作成順
          (a: TellerViewModel, b: TellerViewModel) => {
            return b._teller.createTime.getTime() - a._teller.createTime.getTime();
          }
        : // 請求数順
          (a: TellerViewModel, b: TellerViewModel) => {
            return (b._teller.invoiceAmount?.phone ?? 0) - (a._teller.invoiceAmount?.phone ?? 0);
          };
    tmpTeller = tmpTeller.sort(sortFn);

    return tmpTeller;
  }, [tellers, keyword, filter, sort]);

  // 表示する件数
  const [shouNum, setShowNum] = useState(0);

  // 実際に表示するリスト
  const showList = useMemo(() => {
    return filterAllList.slice(0, shouNum);
  }, [filterAllList, shouNum]);

  // 最下が表示されたら、表示する件数を増やす
  const bottomRef = useRef(null);
  const isShowBottom = useInView(bottomRef);
  useEffect(() => {
    if (isShowBottom) {
      setShowNum((p) => p + PER_LOAD_NUM);
    }
  }, [isShowBottom]);

  // すべて表示しきったか
  const isAllShow = useMemo(() => {
    return filterAllList.length === showList.length;
  }, [filterAllList, showList]);

  const commonSelectedClass =
    'cursor-pointer flex justify-center px-1 py-2 rounded text-sm bg-[#3d2748] text-white';
  const commonUnSelectedClass =
    'cursor-pointer flex justify-center px-1 py-2 rounded text-sm bg-[#c9c3cc] text-[#594663]';

  return withLayout(
    <Container>
      <NextSeo
        title="占い師一覧｜ 電話占いRAYSEE｜4,200円分無料、1分220円~｜口コミで当たると評判の電話レイシー"
        description="電話占いRAYSEEに所属している電話占い師の一覧です。電話占いRAYSEEは、合格率5%以下の実力屈指の電話占い師が集結したサービスです。お相手の気持ちや復縁の成就、不倫や複雑な恋愛など、皆様のお悩みに寄り添った鑑定を行います。"
      />
      <Header />
      <Main>
        <div className="space-y-10">
          <div className="p-3">
            <section className="bg-phone-bg2 p-3 space-y-3">
              <h2 className="text-phone-primary text-center font-serif font-bold">
                占い師キーワード検索
              </h2>
              <Form
                onSubmit={handleSubmit}
                render={({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="flex space-x-2">
                      <Field
                        name="keyword"
                        component="input"
                        className="grow rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-red-300 focus:outline-none focus:ring focus:ring-red-200 focus:ring-opacity-50 disabled:bg-gray-100"
                        placeholder="占い師名、占い術など"
                      />

                      <button
                        type="submit"
                        className=" bg-phone-primary rounded-md border border-transparent text-white transition px-5 py-2.5"
                      >
                        検索
                      </button>
                    </div>
                  </form>
                )}
              />
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-row items-center justify-center gap-2">
                  <FilterIcon />
                  <span>絞り込む</span>
                </div>
                <div
                  className={filter.includes('now') ? commonSelectedClass : commonUnSelectedClass}
                  onClick={() => {
                    onFilter('now');
                  }}
                >
                  今すぐ占う
                </div>
                <div
                  className={filter.includes('book') ? commonSelectedClass : commonUnSelectedClass}
                  onClick={() => {
                    onFilter('book');
                  }}
                >
                  予約できる
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-row items-center justify-center gap-2">
                  <SortIcon />
                  並び替え
                </div>
                <div
                  className={sort === 'debut' ? commonSelectedClass : commonUnSelectedClass}
                  onClick={() => onSort('debut')}
                >
                  デビュー順
                </div>
                <div
                  className={sort === 'num' ? commonSelectedClass : commonUnSelectedClass}
                  onClick={() => onSort('num')}
                >
                  相談件数
                </div>
              </div>
            </section>

            <section>
              <Title title="占い師一覧" isCenter />
              <TellerGridList data={showList} displayType="phone" />

              <div className="h-1" ref={bottomRef}></div>
              {!isAllShow && (
                <div>
                  <Loading size={12} color="border-t-rose-400" />
                </div>
              )}
            </section>
          </div>
          <div className="bg-phone-brand h-10" />
        </div>
        <Footer />
      </Main>
      <TabMenu isLogin={!!user} />
    </Container>,
  );
};

export default Page;

// DOMが表示されたか検知する
const useInView = (ref: RefObject<HTMLElement>) => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, setInView]);

  return inView;
};
